import {
  CellClickedEvent,
  CellEditingStoppedEvent,
  CellEditingStartedEvent,
  BodyScrollEvent,
  ColumnResizedEvent,
  NavigateToNextCellParams,
  CsvExportParams,
  ExcelExportParams,
  ExcelCell,
  AgGridEvent,
  ProcessCellForExportParams,
  GridReadyEvent,
  DisplayedColumnsChangedEvent,
  CellPosition,
  GridApi,
  IMenuActionParams,
  Column,
} from '@ag-grid-community/core';

import type { CellKeyDownEvent, NewColumnsLoadedEvent, RowDataUpdatedEvent } from '@ag-grid-community/core';
import { clamp, defaultTo, isEmpty, isNil, isNumber, isString } from 'lodash';
import moment from 'moment';
import DataGrid, { DataGridProps } from './DataGrid';
import { Renderer } from './Renderer';
import textWidth from 'text-width';
import { worklistStatusMap } from 'src/components/WorklistIcon/WorklistIcon';
import { FilterModelType } from 'src/components/Sidenav/SideNav.slice';

export const MOVE_DIRECTION_LOOKUP = {
  // gets row, column change based on arrow direction
  ['ArrowUp']: [-1, 0],
  ['ArrowDown']: [1, 0],
  ['ArrowLeft']: [0, -1],
  ['ArrowRight']: [0, 1],
  ['w']: [-1, 0],
  ['s']: [1, 0],
  ['a']: [0, -1],
  ['d']: [0, 1],
  ['Enter']: [1, 0],
};

export function onCellClicked(this: DataGrid, event: CellClickedEvent): void {
  if (this.props.extraAgGridProps?.suppressRowClickSelection != true) {
    event.node.setSelected(true);
  }

  if (this.props.onCellClicked && !this.props.getAlternateContextMenu) {
    this.props.onCellClicked(event);
  } else if (this.props.onCellClicked && this.props.getAlternateContextMenu) {
    this.props.onCellClicked(event);
    this.noRenderState.leftOrRightClick = 'left';
    this.showWorklist(event);
  }
}
function getFinalColumnWidth(column: Column, padding = 50) {
  const contentWidth = column.getActualWidth();
  const headerName = column.getColDef().headerName || '';
  const headerWidth = textWidth(headerName, { font: 'Roboto', size: 12 }) + padding;
  return Math.max(contentWidth, headerWidth);
}
function autoResizeColumns(gridApi: GridApi, key: 'single' | 'all', params?: IMenuActionParams) {
  if (key === 'all') {
    gridApi.autoSizeAllColumns();
    const columnLimits = gridApi.getAllGridColumns().map((column) => ({
      key: column.getColId(),
      minWidth: getFinalColumnWidth(column),
    }));
    gridApi.sizeColumnsToFit({ columnLimits });
  } else if (key === 'single' && params?.column) {
    const column = params.column;
    const colId = column.getColId();
    gridApi.autoSizeColumns([colId]);

    const finalColumnWidth = getFinalColumnWidth(column);
    params.api.setColumnWidths([{ key: colId, newWidth: finalColumnWidth }]);
  }
}
export function getMainMenuItems(this: DataGrid) {
  return [
    'pinSubMenu', // Submenu for pinning. Always shown.
    {
      name: 'Autosize This Column',
      action: (params: IMenuActionParams) => {
        if (this.noRenderState.gridApi) {
          autoResizeColumns(this.noRenderState.gridApi, 'single', params);
        }
      },
    },
    // Auto-size all columns. Custom function as built-in doesn't work correctly
    {
      name: 'Autosize All Columns',
      action: () => {
        if (this.noRenderState.gridApi) {
          autoResizeColumns(this.noRenderState.gridApi, 'all');
        }
      },
    },
    'resetColumns', // Reset column details. Always shown.
    'expandAll', // Expand all groups. Only shown if grouping by at least one column.
    'contractAll', // Contract all groups. Only shown if grouping by at least one column.
  ];
}
export function onCellEditingStopped(this: DataGrid, event: CellEditingStoppedEvent): void {
  if (this.props.onCellEditingStopped) {
    this.props.onCellEditingStopped(event);
  }
}
export function onCellEditingStarted(this: DataGrid, event: CellEditingStartedEvent): void {
  if (this.props.onCellEditingStarted) {
    this.props.onCellEditingStarted(event);
  }
}
export function onBodyScroll(this: DataGrid, event: BodyScrollEvent): void {
  if (this.props.onBodyScroll) {
    this.props.onBodyScroll(event);
  }
}
export function onColumnResized(this: DataGrid, event: ColumnResizedEvent): void {
  if (event.finished && event.api) {
    event.api.redrawRows();
  }
}

export function getCellFromRowCol(
  this: DataGrid,
  rowChange: number,
  colChange: number,
  previousCellDef: CellPosition
): CellPosition {
  if (!this.noRenderState.columnApi || !this.noRenderState.gridApi) {
    throw new Error('No grid apis found when moving, this shouldnt happen');
  }

  const colChangeLookup = {
    [-1]: this.noRenderState.columnApi.getDisplayedColBefore(previousCellDef.column),
    0: previousCellDef.column,
    1: this.noRenderState.columnApi.getDisplayedColAfter(previousCellDef.column),
  };

  // this gives us the last ag-grid row index (this returns rows.length)
  const lastRowIndex = this.noRenderState.gridApi.getDisplayedRowCount() - 1;

  // this can return null on the left/right side of the screen, but the type doesn't reflect that
  // fallback to the previous col if we get a null for the next one
  const nextColumn = colChangeLookup[colChange] ? colChangeLookup[colChange] : previousCellDef.column;
  const nextRow = clamp(previousCellDef.rowIndex + rowChange, 0, lastRowIndex);

  return {
    rowPinned: undefined,
    rowIndex: nextRow,
    column: nextColumn,
  };
}

export function onCellKeyDown(this: DataGrid, e: CellKeyDownEvent) {
  const event = e.event as unknown as React.KeyboardEvent;
  const isEditingCell = this.noRenderState.gridApi?.getEditingCells();
  if (event && MOVE_DIRECTION_LOOKUP[event.key] && this.noRenderState.gridApi && isEmpty(isEditingCell)) {
    e.event?.preventDefault();
    e.event?.stopPropagation();
    const key = event.key;
    const previousCell = this.noRenderState.gridApi.getFocusedCell();
    let [moveRow, moveColumn] = MOVE_DIRECTION_LOOKUP[key];
    if (!isNil(previousCell)) {
      if (event.shiftKey) {
        // flip everything when shift is held
        moveRow = moveRow * -1;
        moveColumn = moveColumn * -1;
      }

      const nextCellDef = this.getCellFromRowCol(moveRow, moveColumn, previousCell);
      this.noRenderState.gridApi.clearRangeSelection();
      this.noRenderState.gridApi.clearFocusedCell();
      this.noRenderState.gridApi.setFocusedCell(nextCellDef.rowIndex, nextCellDef.column, undefined);
      this.noRenderState.gridApi.addCellRange({
        rowStartIndex: nextCellDef.rowIndex,
        rowEndIndex: nextCellDef.rowIndex,
        columnStart: nextCellDef.column,
        columnEnd: nextCellDef.column,
      });
    }
  }
}

export function handleNavigateToNextCell(this: DataGrid, _params: NavigateToNextCellParams): CellPosition {
  // this is basically a well-typed noop()
  // all keyboard movement functionality is handled by onCellKeyDown()
  if (!this.noRenderState.gridApi) {
    throw new Error('No columnapi found when moving, this shouldnt happen');
  }
  return this.noRenderState.gridApi.getFocusedCell()!;
}
function getStatusForItem(itemId: string, itemsStatus: DataGridProps['itemsStatus']): string | null {
  if (!itemsStatus) {
    return 'Unknown Status'; // Handle case when itemsStatus is undefined
  }
  const { planning, worklist } = itemsStatus;

  // Check if the item exists in the planning array
  const planningItem = planning.find((item) => item.id === itemId);
  if (planningItem) {
    return planningItem.status; // Return the status from the planning array
  }

  // Check if the item exists in the worklist array
  const worklistItem = worklist.find((item) => item.product === itemId);
  if (worklistItem) {
    // Map the worklist type to a status using the worklistStatusMap
    return worklistStatusMap[worklistItem.type] || 'Unknown Worklist Status';
  }

  // If the item is not found in either, return a default value
  return null;
}
export function onExcelExport(this: DataGrid) {
  const exportOptions = this.props.exportOptions;
  const processCellOverride = exportOptions && exportOptions.processCellOverride;
  const showGroupTitlesExcel = exportOptions && exportOptions.showGroupTitlesExcel;
  const itemsStatus = this.props.itemsStatus;
  let excelOptions: ExcelExportParams = {
    sheetName: 'S5 Assortment Export',
    processCellCallback: function (params: ProcessCellForExportParams) {
      const { column, value, node } = params;
      const colId = column.getColId();
      let cellRenderer = column.getColDef()['renderer'] || column.getColDef()['cellRenderer']; //|| node?.data.renderer; // renderers do not appear in transposed grids

      if (isNil(cellRenderer) && isNumber(value)) {
        cellRenderer = node ? node.data.formatter : undefined;
      }
      //convert to string status adorments column value when export excel
      if (cellRenderer === 'adornmentsGridRenderer') {
        const itemStatus = getStatusForItem(params.value, itemsStatus);
        return itemStatus;
      }

      const renderer = typeof cellRenderer === 'string' ? cellRenderer : '';
      const renderFn = renderer ? Renderer[renderer] : undefined;
      const isFirstColumn = colId === 'ag-Grid-AutoColumn';

      if (isFirstColumn && showGroupTitlesExcel) {
        // return the group name for all the group column cells if configured
        return node?.data.group[0];
      }

      // Handle group columns correctly for export when nested
      if (isFirstColumn && node?.group && node?.groupData) {
        // Only show the current level value
        return node.groupData[colId];
      }

      if (processCellOverride) {
        const possibleNewValue = processCellOverride(params);
        if (possibleNewValue) {
          return possibleNewValue;
        }
      }

      if (exportOptions && exportOptions.excelRendererObj && renderer !== '') {
        const excelFormat = exportOptions.excelRendererObj[renderer];
        if (excelFormat) {
          const columnDataType = column.getColDef().cellDataType === 'number';
          const isValueNumber =
            columnDataType &&
            (!isNumber(params.value) || value === 'NaN' || value === '-Infinity' || value === 'Infinity');
          return !isValueNumber ? value : null;
        }
      }

      return !isFirstColumn && renderFn ? renderFn(value) : value;
    },
  };

  if (this.props.exportOptions) {
    const { customHeader, fileName } = this.props.exportOptions;
    const header: ExcelCell[] = [
      {
        data: {
          type: 'String',
          value: customHeader as string | null,
        },
        mergeAcross: 5,
      },
    ];

    excelOptions = {
      ...excelOptions,
      prependContent: customHeader
        ? [
            {
              cells: header,
            },
          ]
        : undefined,
      fileName: fileName && fileName + ' at ' + moment().format('YYYY-MM-DD hh|mmA'),
    };
  }

  if (this.noRenderState.gridApi) {
    this.noRenderState.gridApi.exportDataAsExcel(excelOptions);
  }
}

export function onCsvExport(this: DataGrid) {
  const processCellOverride = this.props.exportOptions && this.props.exportOptions.processCellOverride;
  let csvOptions: CsvExportParams = {
    processCellCallback: function (params: ProcessCellForExportParams) {
      const { column, value } = params;
      const colId = column.getColId();
      const cellRenderer = column.getColDef()['renderer'] || column.getColDef()['cellRenderer'];
      const renderer = typeof cellRenderer === 'string' ? cellRenderer : '';
      const renderFn = renderer ? Renderer[renderer] : undefined;
      const isColorColumn = colId.indexOf('band_color') >= 0;

      // Change HEX color codes to N/A.
      if (isColorColumn) {
        return 'N/A';
      }

      if (processCellOverride) {
        const possibleNewValue = processCellOverride(params);
        if (possibleNewValue) {
          return possibleNewValue;
        }
      }
      return renderFn ? renderFn(value) : value;
    },
  };

  if (this.props.exportOptions) {
    const { customHeader, fileName } = this.props.exportOptions;
    csvOptions = {
      ...csvOptions,
      prependContent: customHeader && customHeader + '\n',
      fileName: fileName && fileName + ' at ' + moment().format('YYYY-MM-DD hh|mmA'),
    };
  }

  if (this.noRenderState.gridApi) {
    this.noRenderState.gridApi.exportDataAsCsv(csvOptions);
  }
}

export function onGridReady(this: DataGrid, event: GridReadyEvent) {
  this.noRenderState.gridApi = event.api;
  this.noRenderState.columnApi = event.columnApi;

  event.api.updateGridOptions({ columnDefs: this.processColDefs() });

  const { defaultShownValues } = this.noRenderState;
  if (!this.props.loaded) {
    event.api.showLoadingOverlay();
  }
  // when applicable apply default sorts/filters falling back to favorite values
  if (defaultShownValues) {
    event.api.setFilterModel(defaultShownValues);
  }
  if (!isEmpty(this.props.gridFilterModel)) {
    event.api.setFilterModel(this.props.gridFilterModel);
  }
  if (!isEmpty(this.props.gridSortModel)) {
    event.columnApi.applyColumnState({ state: this.props.gridSortModel });
  }

  if (this.props.isPrintMode) {
    event.api.setDomLayout('print');
  }

  if (this.props.onGridReady) {
    this.props.onGridReady(event);
  }

  // at times even though the data prop has been modified and updated via gridApi
  // the grid will still not render any data so this forces it on grid ready to refresh
  // this should be called after `this.props.onGridReady` so that parent components have their
  // gridApi when data is set, which may be needed to calculated side effects
  event.api.updateGridOptions({ rowData: this.props.data });
}

export function onNewColumnsLoaded(this: DataGrid, event: NewColumnsLoadedEvent) {
  if (this.props.onNewColumnsLoaded) {
    this.props.onNewColumnsLoaded(event);
  }
}

export function onDisplayedColumnsChanged(this: DataGrid, event: DisplayedColumnsChangedEvent) {
  if (this.props.onDisplayedColumnsChanged) {
    this.props.onDisplayedColumnsChanged(event);
  }
}
/**
 * Merges the `newFilterModel` with the previous `oldFilterModel`,
 * preventing an empty array override for "set" filters.
 *
 *  - If `newFilterModel[colId]` is { filterType: 'set', values: [] }
 *    but `oldFilterModel[colId]` had a non-empty array, we restore the old array. ('keeping' the selection)
 *  - This effectively stops the forced emptying of the selection just
 *    because the data changed and the set filter didn’t find matching values.
 */
function mergeSetFilterModels(newFilterModel: FilterModelType, oldFilterModel?: FilterModelType) {
  // If one is missing, skip
  if (!oldFilterModel || !newFilterModel) {
    return newFilterModel;
  }

  // Clone so that we can modify it safely
  const mergedModel = { ...newFilterModel };

  // Iterate over all columns in the new filter model
  Object.keys(mergedModel).forEach((colId) => {
    const newFilter = mergedModel[colId];
    const oldFilter = oldFilterModel[colId];

    if (
      newFilter?.filterType === 'set' && // Must be a Set Filter
      Array.isArray(newFilter.values) &&
      newFilter.values.length === 0 && // It's trying to become empty b/c the data to the grid has changed
      oldFilter?.filterType === 'set' &&
      Array.isArray(oldFilter.values) &&
      oldFilter.values.length > 0 // We used to have a non-empty selection
    ) {
      // Reapply the old selection array
      mergedModel[colId] = {
        ...newFilter,
        values: oldFilter.values,
      };
    }
  });

  return mergedModel;
}

export function onFilterChanged(this: DataGrid, event: AgGridEvent) {
  const newFilterModel = event.api.getFilterModel();
  const oldFilterModel = this.props.gridFilterModel;

  // Merge the filters to 'keep' selections on data change (this was implemented with INT-4683 - filter selections weren't holding on flowStatus changes)
  const merged = mergeSetFilterModels(newFilterModel, oldFilterModel);

  if (this.props.storeGridFilterModel) {
    this.props.storeGridFilterModel(merged);
  }
}

export function onSortChanged(this: DataGrid, event: AgGridEvent) {
  const currentSortModel = event.api.getColumnState();
  if (this.props.storeGridSortModel) {
    this.props.storeGridSortModel(currentSortModel);
  }
}

export function onRowDataUpdated(this: DataGrid, event: RowDataUpdatedEvent) {
  if (!isEmpty(this.props.gridFilterModel)) {
    event.api.setFilterModel(this.props.gridFilterModel);
  }
  if (!isEmpty(this.props.gridSortModel)) {
    event.columnApi.applyColumnState({ state: this.props.gridSortModel });
  }
}
